// Entity chips: when an ASSISTANT message contains a markdown link to one of
// our own objects (apps.nownia.com/time/…, nownia.com/time/…, orion://time/…)
// and the link is STANDALONE — the link is the entire rendered paragraph — the
// bare link renders as a tappable entity card row instead (type glyph + the
// link's text). Inline in-sentence links stay ordinary links.
//
// This module is the pure classification seam: hast-node standalone detection
// (Markdown.tsx hands us the paragraph node react-markdown parsed) + glyph
// kind by path segment. Navigation itself reuses `resolveInAppPath`.

export type EntityKind = 'task' | 'event' | 'dashboard' | 'contact' | 'app';

// Hosts that mark a link as "ours". `apps.nownia.com` is canonical; the
// assistant occasionally emits the singular `app.nownia.com` and the bare
// `nownia.com` / `www.nownia.com` — treat all as in-app.
const IN_APP_HOSTS = new Set(['apps.nownia.com', 'app.nownia.com', 'nownia.com', 'www.nownia.com']);

// The `time/...` segments of an in-app link, or null when the href isn't one.
// Mirrors app-link.ts's parser but tolerates the bare nownia.com host too —
// classification is broader than routing on purpose (an unrouted in-app link
// still deserves the chip treatment; its tap falls back to the raw href).
export function inAppSegments(href: string | undefined | null): string[] | null {
  if (!href) return null;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (url.protocol === 'orion:') {
    if (url.host.toLowerCase() !== 'time') return null;
    return url.pathname.split('/').filter(Boolean);
  }
  if ((url.protocol === 'https:' || url.protocol === 'http:') && IN_APP_HOSTS.has(url.hostname.toLowerCase())) {
    const segments = url.pathname.split('/').filter(Boolean);
    if (segments[0]?.toLowerCase() !== 'time') return null;
    return segments.slice(1);
  }
  return null;
}

// Glyph classification by path segment — rules checked in order, first match
// wins (a path with both `task` and `calendar` segments is a task); any other
// in-app destination gets the generic app/arrow glyph. Keep the rule table in
// step with the native EntityChipRow classifiers.
const KIND_RULES: readonly [EntityKind, ReadonlySet<string>][] = [
  ['task', new Set(['task', 'tasks', 'needs-input'])],
  ['event', new Set(['event', 'events', 'calendar', 'calendars'])],
  ['dashboard', new Set(['dashboard', 'dashboards'])],
  ['contact', new Set(['contact', 'contacts'])],
];

export function entityKindForHref(href: string | undefined | null): EntityKind | null {
  const segments = inAppSegments(href);
  if (!segments) return null;
  const lowered = segments.map((s) => s.toLowerCase());
  for (const [kind, names] of KIND_RULES) {
    if (lowered.some((s) => names.has(s))) return kind;
  }
  return 'app';
}

// ===== standalone detection over the hast paragraph node =====
//
// react-markdown passes each renderer the hast node it came from. A paragraph
// is a standalone link when its children are exactly one <a> element (pure
// whitespace text around it tolerated). Anything else — leading text, a second
// link, trailing punctuation — keeps the ordinary inline-link rendering.

interface HastText {
  type: 'text';
  value: string;
}
interface HastElement {
  type: 'element';
  tagName: string;
  properties?: { href?: string | number | boolean | (string | number)[] };
  children?: HastNode[];
}
export type HastNode = HastText | HastElement | { type: string; [key: string]: unknown };

export interface StandaloneEntityLink {
  href: string;
  text: string;
  kind: EntityKind;
}

function isWhitespaceText(node: HastNode): boolean {
  return node.type === 'text' && typeof (node as HastText).value === 'string' && (node as HastText).value.trim() === '';
}

function textOf(node: HastNode): string {
  if (node.type === 'text') return (node as HastText).value ?? '';
  const children = (node as HastElement).children;
  if (!Array.isArray(children)) return '';
  return children.map(textOf).join('');
}

// The paragraph's standalone in-app link, or null. `node` is the hast element
// react-markdown hands the `p` renderer — typed loose (unknown) because hast's
// Element type doesn't structurally match our minimal reading of it; every
// access below is defensively narrowed, so an unusual tree just returns null.
export function standaloneEntityLink(node: unknown): StandaloneEntityLink | null {
  const children = (node as { children?: HastNode[] } | undefined | null)?.children;
  if (!Array.isArray(children)) return null;
  const meaningful = children.filter((c) => !isWhitespaceText(c));
  if (meaningful.length !== 1) return null;
  const only = meaningful[0] as HastElement;
  if (only.type !== 'element' || only.tagName !== 'a') return null;
  const href = typeof only.properties?.href === 'string' ? only.properties.href : null;
  if (!href) return null;
  const kind = entityKindForHref(href);
  if (!kind) return null;
  const text = textOf(only).trim();
  return { href, text: text || href, kind };
}
